// Adapter utilities for converting TaskData to WebTaskItem-compatible format
// This allows TaskInputRenderer (designed for workflow tasks) to work with data editing

import type { TaskData, TaskNote } from '@/lib/sdk/chatbot/types';
import type { WebTaskItem, ValidationRule } from '@/lib/sdk/workflows/types';
import { mapTaskTypeToUiComponent, buildValidationFromType } from './taskTypeMapping';

/**
 * Extended TaskData interface that includes task_type_metadata
 * The API returns this field but it's not in the TypeScript interface
 */
interface TaskDataExtended extends TaskData {
  task_type_metadata?: string;
}

/**
 * Parses choice options from task_type_metadata JSON string
 * Converts to the TaskNote format expected by TaskInputRenderer/ChoiceTask
 */
function parseChoiceMetadata(metadata: string | undefined): TaskNote[] {
  if (!metadata) return [];

  try {
    const parsed = JSON.parse(metadata);
    if (Array.isArray(parsed)) {
      // Convert array of strings to TaskNote-like objects with is_custom_data flag
      return parsed.map((text: string, idx: number) => ({
        id: idx,
        xid: `choice_${idx}`,
        status_text: text,
        is_custom_data: true,
        is_action: false,
        created_at: '',
        updated_at: '',
      }));
    }
  } catch {
    // Invalid JSON, return empty
  }

  return [];
}

/**
 * Adapts TaskData from the data editing API to WebTaskItem format
 * This allows us to reuse TaskInputRenderer with data editing tasks
 *
 * @param task - The TaskData item from useDataGroup
 * @param botCode - The chatbot code for context
 * @returns A WebTaskItem-compatible object
 */
export function adaptTaskDataForInput(task: TaskDataExtended, botCode: string): WebTaskItem {
  const uiComponent = mapTaskTypeToUiComponent(task.type);
  const validation = buildValidationFromType(task.type);

  // Build notes array for choice tasks
  const notes = parseChoiceMetadata(task.task_type_metadata);

  return {
    task: {
      id: task.id.toString(),
      task_id: task.id.toString(),
      task_name: task.name,
      chatbot_code: botCode,
      rep_type: task.type,
      rep_status: 1, // Always completed for data editing
      notes: notes,
      tags: [],
      updated_at: task.task_date,
      state_changed_at: task.task_date,
      source_key: '',
      task_date: task.task_date,
    },
    ui_component: uiComponent,
    validation: validation,
    is_blocked: !task.mutable, // Non-mutable tasks are blocked
    display_value: task.note || '',
  };
}

/**
 * Builds validation rules from task data
 * Merges type-based validation with any task-specific constraints
 */
export function getTaskValidation(task: TaskDataExtended): ValidationRule {
  const baseValidation = buildValidationFromType(task.type);

  // Could extend this to include min/max from task metadata if available
  return baseValidation;
}

/**
 * Checks if a task has any media attachments
 */
export function hasMediaAttachments(task: TaskData): boolean {
  return Boolean(
    (task.photos && task.photos.length > 0) ||
      (task.videos && task.videos.length > 0) ||
      (task.documents && task.documents.length > 0) ||
      (task.media && task.media.length > 0)
  );
}

/**
 * Gets the total count of media attachments
 */
export function getMediaCount(task: TaskData): number {
  return (
    (task.photos?.length || 0) +
    (task.videos?.length || 0) +
    (task.documents?.length || 0) +
    (task.media?.length || 0)
  );
}
